import { computed, effect, inject, Injectable, signal } from "@angular/core";

import { AuthService } from "src/app/auth/services/auth.service";
import { IUserStorageTree } from "../../interfaces/user-storage-tree.interface";
import { scoped } from "../../functions/scoped";
import { storageRoot } from "../../functions/storage-root.function";
import { storageSignalTree } from "../../functions/storage-signal-tree.function";
import { IPageData, PageData } from "src/app/infrastructure/services/page-services/models/page-data";
import { parseJwt } from "../../functions/parse-jwt";
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

@Injectable({
    providedIn: 'root'
})
export class UserLocalStorageService {
    private readonly __authService = injectRequired(AuthService);

    private __userGuid$$ = signal<string>('');

    // True zodra de user-scope bekend is (auth-token verwerkt) en de tree dus uit localStorage
    // gehydrateerd is. Pas dán is opgeslagen page-/tab-state betrouwbaar te lezen; vóór dat moment
    // staat de tree nog op de lege fallback (scope-key null → niets geladen).
    readonly hydrated$$ = computed(() => this.__userGuid$$().length > 0);

    private __tree$$ = storageSignalTree<IUserStorageTree>(
        () => scoped('user', this.__userGuid$$()),
        {} as IUserStorageTree,
        {
            type: 'local',
            debounce: 300,
            ttl: 86400000
        }
    );

    domainProfileData$$ = this.__tree$$.domainProfile;
    pageStart$$ = this.__tree$$.startPage.ensure(() => PageData.create());

    constructor() {
        effect(() => {
            const __token = this.__authService.token$$();
            if (!__token) return;
            const __payload = parseJwt(__token!);

            this.__userGuid$$.set(__payload.nameid);
        })
    }

    // Directe, niet-reactieve read voor een expliciet gekende user-guid (i.p.v. via __tree$$,
    // dat pas ná de asynchrone scope-hydratie-effect herwijst naar deze guid). Vlak na een
    // account-wissel in dezelfde sessie (logout -> login als andere user, geen page-reload)
    // bevat __tree$$.startPage nog de vórige gebruiker totdat de effect hierboven is gevlushed;
    // AccountPageService.login$ leest de startpagina synchroon direct na de login-response en
    // kan daardoor naar een dauGuid van een ander domain-lidmaatschap navigeren.
    pageStart_forGuid(userGuid: string): IPageData | null {
        const __key = scoped('user', userGuid);
        if (!__key) return null;

        const { rootValue$$ } = storageRoot<IUserStorageTree>(__key, {} as IUserStorageTree, 'local', 300, 86400000);
        return rootValue$$().startPage ?? null;
    }
}